import _extends from "@babel/runtime/helpers/extends";
import React, { memo, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Integer from "../ForceForm/types/Integer";
import List from "../ForceForm/types/List";
import Number from "../ForceForm/types/Number";
import Text from "../ForceForm/types/Text";
import Date from "../ForceForm/types/Date";
import { searchValues } from "../../lib/services/valueLists";
import { useWeb5Context } from "../../lib/Web5Context";
import ConditionsContext from "./ConditionsContext";
const FieldValue = _ref => {
  var _options$listOptions;
  let {
    isReadOnly,
    type,
    className,
    options,
    selectedField,
    table = null,
    onChange = () => null,
    value = null
  } = _ref;
  const {
    t,
    language
  } = useWeb5Context();
  const context = useContext(ConditionsContext);
  const fieldReadOnly = useMemo(() => isReadOnly || context.isReadOnly, [context.isReadOnly, isReadOnly]);
  const boolOptions = useMemo(() => [{
    label: t('cfm_label_yes'),
    value: true
  }, {
    label: t('cfm_label_no'),
    value: false
  }], [t]);
  const handleChange = useCallback(e => {
    var _e$target;
    let aux = (e === null || e === void 0 ? void 0 : (_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.value) || e;
    onChange(aux);
  }, [onChange]);
  const properties = useMemo(() => ({
    value,
    name: 'value',
    onChange: handleChange,
    isReadOnly: fieldReadOnly,
    className
  }), [fieldReadOnly, handleChange, value, className]);
  const onSearchValues = useCallback(function (text) {
    if (text === void 0) {
      text = 'a';
    }
    return searchValues({
      entity: table,
      text,
      lang: language
    }).then(_ref2 => {
      let {
        data
      } = _ref2;
      return data.map(item => ({
        label: item.description,
        value: item.id
      }));
    }).catch(err => {
      console.error(err);
    });
  }, [language, table]);
  const dateProperties = useMemo(() => ({
    value: value === null ? undefined : value,
    name: 'value',
    onChange: handleChange,
    isReadOnly: fieldReadOnly,
    className,
    isFullWidth: true
  }), [fieldReadOnly, handleChange, value, className]);
  switch (type) {
    case 'bit':
      return /*#__PURE__*/React.createElement(List, _extends({}, properties, {
        value: properties.value,
        options: boolOptions,
        isFullWidth: false
      }));
    case 'multivalue':
    case 'list':
      if (properties.value === '') properties.value = null;
      const listProps = options !== null && options !== void 0 && options.listOptions ? {
        table,
        customGetList: options === null || options === void 0 ? void 0 : (_options$listOptions = options.listOptions) === null || _options$listOptions === void 0 ? void 0 : _options$listOptions.customGetList,
        idFieldTable: selectedField === null || selectedField === void 0 ? void 0 : selectedField.value,
        selectedField: selectedField === null || selectedField === void 0 ? void 0 : selectedField.value,
        valueObjectNeeded: {
          table
        }
      } : {
        loadOptions: onSearchValues,
        isFuzzy: true,
        valueObjectNeeded: {
          table
        },
        overrides: {
          'react-select': {
            defaultOptions: true,
            openMenuOnClick: true
          }
        }
      };
      return /*#__PURE__*/React.createElement(List, _extends({}, properties, {
        placeholder: t('cfm_placeholder_search'),
        labelMode: "horizontal",
        isFullWidth: false
      }, listProps));
    case 'currency':
    case 'decimal':
    case 'percentage':
      return /*#__PURE__*/React.createElement(Number, _extends({}, properties, {
        placeholder: t('cfm_placeholder_decimal')
      }));
    case 'int':
      return /*#__PURE__*/React.createElement(Integer, _extends({}, properties, {
        placeholder: t('cfm_placeholder_integer')
      }));
    case 'datetime':
      return /*#__PURE__*/React.createElement(Date, _extends({
        type: "datetime"
      }, dateProperties));
    case 'text':
    default:
      if (typeof properties.value !== 'boolean' && typeof properties.value !== 'number') {
        properties.value = properties.value ? properties.value.toString() : '';
      }
      return /*#__PURE__*/React.createElement(Text, _extends({}, properties, {
        placeholder: t('cfm_placeholder_type_value')
      }));
  }
};
process.env.NODE_ENV !== "production" ? FieldValue.propTypes = {
  table: PropTypes.string,
  onChange: PropTypes.func,
  isReadOnly: PropTypes.bool,
  type: PropTypes.oneOf(['bit', 'text', 'datetime', 'decimal', 'int', 'list', 'multivalue', 'currency', 'percentage']).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.arrayOf(PropTypes.any), PropTypes.bool, PropTypes.object])
} : void 0;
export default /*#__PURE__*/memo(FieldValue);